import React, { useState, useMemo } from 'react';
import {
  Button,
  Stack,
  Typography,
  List,
  ListItemButton,
  Avatar,
  Checkbox,
  CircularProgress,
  Box,
  DialogContent,
  DialogActions,
  IconButton,
  Pagination,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import DialogHeader from '@shared/ui/dialog/DialogHeader';
import { useProducts } from '../../hooks/useProducts';
import { ProductStatus } from '@domain/product/models/ProductFilter';
import { Product } from '@domain/product/models/Product';
import { useProductsFilter } from '@features/products/hooks/useProductsFilter';
import ProductsFilter from '@shared/ui/filters/ProductsFilter';
import ActiveFilters from '@shared/ui/filters/ActiveFilters';
import EmptyState from '@shared/ui/common/EmptyState';
import { APP_CONFIG } from '@config/app.config';

export interface ProductSelectionWithQuantity {
  product: Product;
  quantity: number;
}

export interface ProductSelectBottomSheetProps {
  title?: string;
  initialProducts?: Product[];
  initialSelections?: ProductSelectionWithQuantity[];
  excludeProductIds?: Set<string>;
  getDisabledCaption?: (productId: string) => string;
  confirmButtonText?: (count: number) => string;
  /** When true, confirm is enabled with 0 selections and still calls onFinish([]) */
  allowEmptyConfirm?: boolean;
  onFinish: (selections: ProductSelectionWithQuantity[]) => void | Promise<void>;
  onClose: () => void;
}

const defaultConfirmLabel = (count: number) =>
  `Add ${count} product${count !== 1 ? 's' : ''}`;

const ProductSelectBottomSheet: React.FC<ProductSelectBottomSheetProps> = ({
  title = 'Select products',
  initialProducts = [],
  initialSelections,
  excludeProductIds = new Set(),
  getDisabledCaption,
  confirmButtonText = defaultConfirmLabel,
  allowEmptyConfirm = false,
  onFinish,
  onClose,
}) => {
  const { filterCriteria, page, setPage, limit } = useProductsFilter(50);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedProducts, setSelectedProducts] = useState<
    Map<string, ProductSelectionWithQuantity>
  >(() => {
    const m = new Map<string, ProductSelectionWithQuantity>();
    if (initialSelections?.length) {
      initialSelections.forEach(s => m.set(s.product.id, { ...s }));
    } else {
      initialProducts.forEach(p => m.set(p.id, { product: p, quantity: 1 }));
    }
    return m;
  });

  const { products: productsResponse, isLoading, isFetching } = useProducts(
    { ...filterCriteria, page, limit },
    ProductStatus.ACTIVE
  );

  const products =
    productsResponse?.status === 'success' ? productsResponse.data : [];
  const totalPages =
    productsResponse?.status === 'success' ? productsResponse.totalPages : 0;

  const toggleProduct = (product: Product) => {
    if (excludeProductIds.has(product.id)) return;
    setSelectedProducts(prev => {
      const next = new Map(prev);
      if (next.has(product.id)) next.delete(product.id);
      else next.set(product.id, { product, quantity: 1 });
      return next;
    });
  };

  const captionForExcludedRow = (productId: string) =>
    getDisabledCaption?.(productId) ?? 'Already added';

  const selectableOnPage = useMemo(
    () => products.filter(p => !excludeProductIds.has(p.id)),
    [products, excludeProductIds]
  );

  const selectedCountOnPage = useMemo(
    () => selectableOnPage.filter(p => selectedProducts.has(p.id)).length,
    [selectableOnPage, selectedProducts]
  );

  const allSelectableOnPageSelected =
    selectableOnPage.length > 0 && selectedCountOnPage === selectableOnPage.length;
  const someSelectableOnPageSelected =
    selectedCountOnPage > 0 && !allSelectableOnPageSelected;

  const toggleSelectAllOnPage = () => {
    if (selectableOnPage.length === 0) return;
    setSelectedProducts(prev => {
      const next = new Map(prev);
      if (allSelectableOnPageSelected) {
        selectableOnPage.forEach(p => next.delete(p.id));
      } else {
        selectableOnPage.forEach(p => next.set(p.id, { product: p, quantity: 1 }));
      }
      return next;
    });
  };

  const handleConfirm = async () => {
    const selected = Array.from(selectedProducts.values());
    if (selected.length === 0 && !allowEmptyConfirm) return;
    setIsSubmitting(true);
    try {
      await Promise.resolve(onFinish(selected));
    } finally {
      setIsSubmitting(false);
    }
  };

  const loading = isLoading || isFetching;
  const n = selectedProducts.size;

  return (
    <>
      <DialogHeader>
        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
          <Typography variant="h6" component="div">
            {title}
          </Typography>
          <IconButton onClick={onClose} size="small" disabled={isSubmitting}>
            <Close />
          </IconButton>
        </Stack>
      </DialogHeader>

      <DialogContent sx={{ flex: 1, overflow: 'auto', p: 0 }}>
        <Stack gap={0}>
          <Stack
            direction="row"
            position="sticky"
            top={0}
            justifyContent="flex-start"
            alignItems="center"
            px={4}
            py={2}
            spacing={2}
            borderBottom={1}
            borderColor="divider"
            bgcolor="background.paper"
            zIndex={99}
            flexWrap="wrap"
          >


            <Stack direction="row" alignItems="center" spacing={1}>
              <Checkbox
                checked={allSelectableOnPageSelected}
                indeterminate={someSelectableOnPageSelected}
                disabled={selectableOnPage.length === 0}
                onChange={toggleSelectAllOnPage}
                inputProps={{ 'aria-label': 'Select all products on this page' }}
              />
              <Typography variant="body2" color="text.secondary" component="div">
                ({selectedCountOnPage}/{selectableOnPage.length})
              </Typography>
            </Stack>

            <ProductsFilter />
            <ActiveFilters />

          </Stack>


          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : products.length === 0 ? (
            <EmptyState
              title="No products found"
              description="No products match the current filters"
            />
          ) : (
            <Stack gap={0}>
              <List sx={{ gap: 0, p: 0, width: '100%' }}>
                {products.map(product => {
                  const excluded = excludeProductIds.has(product.id);
                  /** Excluded rows are disabled but show a checked box (already added / not selectable). */
                  const checkboxChecked = excluded || selectedProducts.has(product.id);

                  return (
                    <ListItemButton
                      key={product.id}
                      divider
                      disabled={excluded}
                      onClick={() => toggleProduct(product)}
                      sx={{
                        p: 4,
                        ...(excluded ? { opacity: 0.72, cursor: 'default' } : {}),
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" flex={1} spacing={2}>
                        <Checkbox
                          checked={checkboxChecked}
                          disabled={excluded}
                          onChange={() => toggleProduct(product)}
                          onClick={e => e.stopPropagation()}
                          sx={{ mt: 0.5 }}
                        />
                        <Avatar
                          variant="rounded"
                          sx={{
                            border: 1,
                            borderColor: APP_CONFIG.ui.theme.colors.border,
                            width: 40,
                            height: 40,
                            mt: 0.25,
                          }}
                          src={product.image}
                          alt={product.title}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight="medium"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {product.title || 'Untitled'}
                          </Typography>

                          <Stack direction="row" spacing={1}>
                            {product.ean && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                EAN: {product.ean}
                              </Typography>
                            )}


                            {product.sku && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                SKU: {product.sku}
                              </Typography>
                            )}

                            {excluded ? (
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                {captionForExcludedRow(product.id)}
                              </Typography>
                            ) : null}
                          </Stack>
                        </Box>
                      </Stack>
                    </ListItemButton>
                  );
                })}
              </List>

              {totalPages > 1 && (
                <Stack p={2} direction="row" justifyContent="center" alignItems="center">
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                    color="primary"
                    size="large"
                    shape="rounded"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          variant="contained"
          onClick={() => void handleConfirm()}
          disabled={(n === 0 && !allowEmptyConfirm) || isSubmitting}
        >
          {isSubmitting ? (
            <CircularProgress size={24} />
          ) : (
            confirmButtonText(n)
          )}
        </Button>
      </DialogActions>
    </>
  );
};

export default ProductSelectBottomSheet;
